import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { payrollAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../components/EmployeeManagement.css';

const PayrollManagement = () => {
  const { isPrivileged } = useAuth();
  
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  const [payrolls, setPayrolls] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  
  const initialFormState = {
    _id: '',
    employee: '',
    month: new Date().toISOString().slice(0, 7),
    basicSalary: 0,
    allowances: { hra: 0, transport: 0, medical: 0, special: 0, other: 0 },
    deductions: { tax: 0, providentFund: 0, insurance: 0, loan: 0, other: 0 },
    overtime: { hours: 0, amount: 0 },
    bonus: 0,
    paymentMethod: 'bank-transfer',
    status: 'pending',
    remarks: ''
  };
  
  const [form, setForm] = useState(initialFormState);

  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const [y, m] = filterMonth.split('-');
      const res = await payrollAPI.getAll({ year: y, month: m });
      setPayrolls(res?.data || res || []);
    } catch (e) { 
      setError('Failed to load payroll'); 
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => {
    fetchPayrolls();
    if (isPrivileged) {
      employeeAPI.getAll()
        .then(r => setEmployees(r?.data || r || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth]);

  // Handle nested states changes
  const handleNestedChange = (parentKey, childKey, value) => {
    const val = parseFloat(value) || 0;
    setForm(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: val
      }
    }));
  };

  // Submit Handler (Create and Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const [y, m] = form.month.split('-');
      
      const payload = {
        ...form,
        year: parseInt(y),
        month: parseInt(m),
        basicSalary: parseFloat(form.basicSalary) || 0,
        bonus: parseFloat(form.bonus) || 0,
      };

      if (isEditMode) {
        await payrollAPI.update(form._id, payload);
      } else {
        await payrollAPI.create(payload);
      }
      
      setShowModal(false);
      setIsEditMode(false);
      setForm(initialFormState);
      setError('');
      fetchPayrolls();
    } catch (err) { 
      setError(err?.message || 'Failed to process payroll'); 
    }
  };

  // Delete Handler
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payroll record?')) {
      try {
        await payrollAPI.delete(id);
        fetchPayrolls();
      } catch (err) {
        setError('Failed to delete payroll record');
      }
    }
  };

  // Change Status Quick Handler
  const handleQuickStatusUpdate = async (id, status) => {
    try {
      await payrollAPI.update(id, { status });
      fetchPayrolls();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const startEdit = (p) => {
    setIsEditMode(true);
    setForm({
      _id: p._id,
      employee: p.employee?._id || p.employee,
      month: `${p.year}-${String(p.month).padStart(2, '0')}`,
      basicSalary: p.basicSalary || 0,
      allowances: {
        hra: p.allowances?.hra || 0,
        transport: p.allowances?.transport || 0,
        medical: p.allowances?.medical || 0,
        special: p.allowances?.special || 0,
        other: p.allowances?.other || 0,
      },
      deductions: {
        tax: p.deductions?.tax || 0,
        providentFund: p.deductions?.providentFund || 0,
        insurance: p.deductions?.insurance || 0,
        loan: p.deductions?.loan || 0,
        other: p.deductions?.other || 0,
      },
      overtime: {
        hours: p.overtime?.hours || 0,
        amount: p.overtime?.amount || 0,
      },
      bonus: p.bonus || 0,
      paymentMethod: p.paymentMethod || 'bank-transfer',
      status: p.status || 'pending',
      remarks: p.remarks || ''
    });
    setShowModal(true);
  };

  // Helper formatting currency
  const fmt = (n) => n !== undefined && n !== null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

  // Summaries
  const totalNetPaid = payrolls.filter(p => p.status === 'paid').reduce((s, p) => s + (p.netSalary || 0), 0);
  const totalNetProcessed = payrolls.filter(p => p.status === 'processed').reduce((s, p) => s + (p.netSalary || 0), 0);
  const totalEarnings = payrolls.reduce((s, p) => s + (p.totalEarnings || 0), 0);
  const totalDeductions = payrolls.reduce((s, p) => s + (p.totalDeductions || 0), 0);

  // Auto calculate Net Salary dynamically for the UI process modal
  const calcAllowancesTotal = () => {
    return Object.values(form.allowances).reduce((a, b) => a + b, 0);
  };

  const calcDeductionsTotal = () => {
    return Object.values(form.deductions).reduce((a, b) => a + b, 0);
  };

  const calcNetSalary = () => {
    const earnings = (parseFloat(form.basicSalary) || 0) + calcAllowancesTotal() + (parseFloat(form.overtime?.amount) || 0) + (parseFloat(form.bonus) || 0);
    const deductions = calcDeductionsTotal();
    return earnings - deductions;
  };

  return (
    <div className="payroll-management">
      <div className="module-header">
        <h2>Payroll Management</h2>
        {isPrivileged && (
          <button className="btn btn-primary" onClick={() => { setIsEditMode(false); setForm(initialFormState); setShowModal(true); }}>
            <i className="fas fa-plus"></i> Process Payroll
          </button>
        )}
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Summary Cards */}
      <div className="leave-stats" style={{ marginBottom: '20px' }}>
        <div className="leave-stat"><h4>Total Paid (Net)</h4><p style={{ color: '#10b981' }}>{fmt(totalNetPaid)}</p></div>
        <div className="leave-stat"><h4>Total Processed</h4><p style={{ color: '#3b82f6' }}>{fmt(totalNetProcessed)}</p></div>
        <div className="leave-stat"><h4>Total Allowances</h4><p style={{ color: '#f59e0b' }}>{fmt(totalEarnings - payrolls.reduce((s,p)=>s+(p.basicSalary||0),0))}</p></div>
        <div className="leave-stat"><h4>Total Deductions</h4><p style={{ color: '#ef4444' }}>{fmt(totalDeductions)}</p></div>
      </div>

      {/* Filters */}
      <div className="filter-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', background: '#fff', padding: '15px 20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <div className="filter-group" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
            <label style={{ margin: 0, fontWeight: 600 }}>Filter Month:</label>
            <input type="month" className="form-input" style={{ width: '180px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #d1d5db' }} value={filterMonth} onChange={e => setFilterMonth(e.target.value)} />
          </div>
        </div>
        <div>
          <button className="btn btn-secondary" onClick={fetchPayrolls}><i className="fas fa-sync"></i> Refresh</button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i><p style={{ marginTop: '10px' }}>Loading payroll records...</p></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {isPrivileged && <th>Emp ID</th>}
                {isPrivileged && <th>Name</th>}
                <th>Month/Year</th>
                <th>Basic Salary</th>
                <th>Total Earnings</th>
                <th>Total Deductions</th>
                <th>Net Salary</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payrolls.length === 0
                ? <tr><td colSpan={isPrivileged ? "9" : "7"} style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No payroll records found for this period</td></tr>
                : payrolls.map(p => (
                  <tr key={p._id}>
                    {isPrivileged && <td>{p.employee?.employeeId || '—'}</td>}
                    {isPrivileged && <td>{p.employee?.name || '—'}</td>}
                    <td>{String(p.month).padStart(2, '0')}/{p.year}</td>
                    <td>{fmt(p.basicSalary)}</td>
                    <td>{fmt(p.totalEarnings)}</td>
                    <td>{fmt(p.totalDeductions)}</td>
                    <td style={{ fontWeight: 700, color: '#111827' }}>{fmt(p.netSalary)}</td>
                    <td>
                      <span style={{ 
                        padding: '4px 10px', 
                        borderRadius: 20, 
                        fontSize: 12, 
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        background: p.status === 'paid' ? '#d1fae5' : p.status === 'processed' ? '#dbeafe' : p.status === 'on-hold' ? '#fee2e2' : '#fef3c7',
                        color: p.status === 'paid' ? '#065f46' : p.status === 'processed' ? '#1e40af' : p.status === 'on-hold' ? '#991b1b' : '#92400e',
                      }}>{p.status || 'pending'}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button 
                          className="btn-icon" 
                          onClick={() => { setSelectedPayroll(p); setShowViewModal(true); }}
                          title="View Payslip"
                          style={{ color: '#4b5563', padding: '6px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        
                        {isPrivileged && (
                          <>
                            <button 
                              className="btn-icon" 
                              onClick={() => startEdit(p)}
                              title="Edit Record"
                              style={{ color: '#2563eb', padding: '6px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>

                            {p.status === 'processed' && (
                              <button 
                                className="btn-icon" 
                                onClick={() => handleQuickStatusUpdate(p._id, 'paid')}
                                title="Mark as Paid"
                                style={{ color: '#10b981', padding: '6px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}
                              >
                                <i className="fas fa-check-circle"></i>
                              </button>
                            )}

                            {p.status === 'pending' && (
                              <button 
                                className="btn-icon" 
                                onClick={() => handleQuickStatusUpdate(p._id, 'processed')}
                                title="Process/Approve"
                                style={{ color: '#3b82f6', padding: '6px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}
                              >
                                <i className="fas fa-arrow-right"></i>
                              </button>
                            )}
                            
                            <button 
                              className="btn-icon" 
                              onClick={() => handleDelete(p._id)}
                              title="Delete Record"
                              style={{ color: '#ef4444', padding: '6px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer' }}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / Create Process Modal */}
      {isPrivileged && showModal && (
        <Modal show={showModal} onClose={() => setShowModal(false)} title={isEditMode ? "Edit Payroll Record" : "Process Monthly Payroll"}>
          <form onSubmit={handleSubmit} style={{ maxHeight: '80vh', overflowY: 'auto', paddingRight: '5px' }}>
            <div className="form-group">
              <label>Select Employee</label>
              <select 
                className="form-input" 
                value={form.employee} 
                onChange={e => setForm({ ...form, employee: e.target.value })} 
                required 
                disabled={isEditMode}
              >
                <option value="">Choose Employee</option>
                {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>)}
              </select>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Month / Period</label>
                <input type="month" className="form-input" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Basic Salary (₹)</label>
                <input type="number" className="form-input" placeholder="50000" value={form.basicSalary} onChange={e => setForm({ ...form, basicSalary: parseFloat(e.target.value) || 0 })} required />
              </div>
            </div>

            <h4 style={{ margin: '15px 0 10px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '5px', color: '#111827' }}>Allowances Structure (₹)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>HRA Allowance</label>
                <input type="number" className="form-input" placeholder="0" value={form.allowances.hra} onChange={e => handleNestedChange('allowances', 'hra', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Transport Allowance</label>
                <input type="number" className="form-input" placeholder="0" value={form.allowances.transport} onChange={e => handleNestedChange('allowances', 'transport', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Medical Allowance</label>
                <input type="number" className="form-input" placeholder="0" value={form.allowances.medical} onChange={e => handleNestedChange('allowances', 'medical', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Special Allowance</label>
                <input type="number" className="form-input" placeholder="0" value={form.allowances.special} onChange={e => handleNestedChange('allowances', 'special', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Other Allowance</label>
              <input type="number" className="form-input" placeholder="0" value={form.allowances.other} onChange={e => handleNestedChange('allowances', 'other', e.target.value)} />
            </div>

            <h4 style={{ margin: '20px 0 10px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '5px', color: '#111827' }}>Deductions Structure (₹)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Income Tax (TDS)</label>
                <input type="number" className="form-input" placeholder="0" value={form.deductions.tax} onChange={e => handleNestedChange('deductions', 'tax', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Provident Fund (PF)</label>
                <input type="number" className="form-input" placeholder="0" value={form.deductions.providentFund} onChange={e => handleNestedChange('deductions', 'providentFund', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Health Insurance</label>
                <input type="number" className="form-input" placeholder="0" value={form.deductions.insurance} onChange={e => handleNestedChange('deductions', 'insurance', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Loan Recovery / Advance</label>
                <input type="number" className="form-input" placeholder="0" value={form.deductions.loan} onChange={e => handleNestedChange('deductions', 'loan', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Other Deductions</label>
              <input type="number" className="form-input" placeholder="0" value={form.deductions.other} onChange={e => handleNestedChange('deductions', 'other', e.target.value)} />
            </div>

            <h4 style={{ margin: '20px 0 10px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '5px', color: '#111827' }}>Overtime & Bonuses (₹)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Overtime Hours</label>
                <input type="number" className="form-input" placeholder="0" value={form.overtime.hours} onChange={e => handleNestedChange('overtime', 'hours', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Overtime Amount (₹)</label>
                <input type="number" className="form-input" placeholder="0" value={form.overtime.amount} onChange={e => handleNestedChange('overtime', 'amount', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Performance Bonus (₹)</label>
                <input type="number" className="form-input" placeholder="0" value={form.bonus} onChange={e => setForm({ ...form, bonus: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label>Payment Method</label>
                <select className="form-input" value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })}>
                  <option value="bank-transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="check">Cheque</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
            </div>

            <div className="form-row" style={{ marginTop: '10px' }}>
              <div className="form-group">
                <label>Status</label>
                <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="pending">Pending</option>
                  <option value="processed">Processed / Approved</option>
                  <option value="paid">Paid</option>
                  <option value="on-hold">On Hold</option>
                </select>
              </div>
              <div className="form-group">
                <label>Remarks</label>
                <input type="text" className="form-input" placeholder="Monthly payroll" value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} />
              </div>
            </div>

            {/* Dynamic salary summary details */}
            <div style={{ marginTop: '20px', background: '#f9fafb', padding: '15px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span>Basic Salary:</span>
                <strong>{fmt(form.basicSalary)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span>Total Allowances (+):</span>
                <strong>{fmt(calcAllowancesTotal())}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                <span>Bonus & Overtime (+):</span>
                <strong>{fmt((parseFloat(form.bonus) || 0) + (parseFloat(form.overtime?.amount) || 0))}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#dc2626' }}>
                <span>Total Deductions (-):</span>
                <strong>{fmt(calcDeductionsTotal())}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #d1d5db', fontSize: '16px', fontWeight: 800, color: '#111827' }}>
                <span>Estimated Net Salary:</span>
                <span>{fmt(calcNetSalary())}</span>
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '20px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">{isEditMode ? "Save Changes" : "Create Record"}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Payslip View Modal (Ready for Print / PDF Download) */}
      {showViewModal && selectedPayroll && (
        <Modal show={showViewModal} onClose={() => { setSelectedPayroll(null); setShowViewModal(false); }} title="Salary Payslip Details">
          <div className="print-payslip-modal" style={{ padding: '10px' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '15px' }}>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#1e3a8a' }}>RIDEAL MOBILITY</h2>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>RiDeal Group Of Companies</span>
              <h3 style={{ margin: '10px 0 0 0', fontSize: '16px', textTransform: 'uppercase', letterSpacing: '1px', color: '#4b5563' }}>
                Salary Payslip for {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][selectedPayroll.month - 1]} {selectedPayroll.year}
              </h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px', fontSize: '13px', background: '#f9fafb', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div>
                <p style={{ margin: '4px 0' }}><strong>Employee Name:</strong> {selectedPayroll.employee?.name || '—'}</p>
                <p style={{ margin: '4px 0' }}><strong>Employee ID:</strong> {selectedPayroll.employee?.employeeId || '—'}</p>
                <p style={{ margin: '4px 0' }}><strong>Department:</strong> {selectedPayroll.employee?.department || '—'}</p>
              </div>
              <div>
                <p style={{ margin: '4px 0' }}><strong>Designation:</strong> {selectedPayroll.employee?.designation || '—'}</p>
                <p style={{ margin: '4px 0' }}><strong>Payment Method:</strong> <span style={{ textTransform: 'capitalize' }}>{(selectedPayroll.paymentMethod || 'bank-transfer').replace('-', ' ')}</span></p>
                <p style={{ margin: '4px 0' }}><strong>Status:</strong> <span style={{ textTransform: 'capitalize', fontWeight: 'bold', color: selectedPayroll.status === 'paid' ? '#10b981' : '#f59e0b' }}>{selectedPayroll.status}</span></p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '20px' }}>
              {/* Earnings Column */}
              <div>
                <h4 style={{ background: '#eff6ff', padding: '8px 12px', margin: '0 0 8px 0', borderLeft: '4px solid #3b82f6', color: '#1e40af' }}>Earnings Structure</h4>
                <div className="payslip-row"><span>Basic Salary</span><span>{fmt(selectedPayroll.basicSalary)}</span></div>
                <div className="payslip-row"><span>HRA Allowance</span><span>{fmt(selectedPayroll.allowances?.hra || 0)}</span></div>
                <div className="payslip-row"><span>Transport Allowance</span><span>{fmt(selectedPayroll.allowances?.transport || 0)}</span></div>
                <div className="payslip-row"><span>Medical Allowance</span><span>{fmt(selectedPayroll.allowances?.medical || 0)}</span></div>
                <div className="payslip-row"><span>Special Allowance</span><span>{fmt(selectedPayroll.allowances?.special || 0)}</span></div>
                <div className="payslip-row"><span>Other Allowance</span><span>{fmt(selectedPayroll.allowances?.other || 0)}</span></div>
                <div className="payslip-row"><span>Overtime Pay ({selectedPayroll.overtime?.hours || 0} hrs)</span><span>{fmt(selectedPayroll.overtime?.amount || 0)}</span></div>
                <div className="payslip-row"><span>Performance Bonus</span><span>{fmt(selectedPayroll.bonus || 0)}</span></div>
                <div className="payslip-row total" style={{ borderTop: '1px solid #d1d5db', marginTop: '6px', paddingTop: '6px', fontWeight: 'bold' }}>
                  <span>Total Earnings (A)</span><span>{fmt(selectedPayroll.totalEarnings)}</span>
                </div>
              </div>

              {/* Deductions Column */}
              <div>
                <h4 style={{ background: '#fef2f2', padding: '8px 12px', margin: '0 0 8px 0', borderLeft: '4px solid #ef4444', color: '#991b1b' }}>Deductions Structure</h4>
                <div className="payslip-row"><span>Income Tax (TDS)</span><span>{fmt(selectedPayroll.deductions?.tax || 0)}</span></div>
                <div className="payslip-row"><span>Provident Fund (PF)</span><span>{fmt(selectedPayroll.deductions?.providentFund || 0)}</span></div>
                <div className="payslip-row"><span>Health Insurance</span><span>{fmt(selectedPayroll.deductions?.insurance || 0)}</span></div>
                <div className="payslip-row"><span>Loan Recovery / Adv</span><span>{fmt(selectedPayroll.deductions?.loan || 0)}</span></div>
                <div className="payslip-row"><span>Other Deductions</span><span>{fmt(selectedPayroll.deductions?.other || 0)}</span></div>
                {/* Spacer to align heights */}
                <div className="payslip-row"><span>&nbsp;</span><span>&nbsp;</span></div>
                <div className="payslip-row"><span>&nbsp;</span><span>&nbsp;</span></div>
                <div className="payslip-row"><span>&nbsp;</span><span>&nbsp;</span></div>
                <div className="payslip-row total" style={{ borderTop: '1px solid #d1d5db', marginTop: '6px', paddingTop: '6px', fontWeight: 'bold' }}>
                  <span>Total Deductions (B)</span><span>{fmt(selectedPayroll.totalDeductions)}</span>
                </div>
              </div>
            </div>

            <div style={{ background: '#3b82f6', color: '#fff', padding: '15px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '18px', fontWeight: 800, marginBottom: '20px' }}>
              <span>NET PAYABLE SALARY (A - B)</span>
              <span>{fmt(selectedPayroll.netSalary)}</span>
            </div>

            {selectedPayroll.remarks && (
              <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#6b7280', marginBottom: '20px' }}>
                Note: {selectedPayroll.remarks}
              </div>
            )}

            <div className="form-actions no-print" style={{ justifyContent: 'space-between', borderTop: '1px solid #e5e7eb', paddingTop: '15px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => { setSelectedPayroll(null); setShowViewModal(false); }}>Close</button>
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                <i className="fas fa-print"></i> Print / Download PDF
              </button>
            </div>
          </div>
        </Modal>
      )}

      <style jsx>{`
        .payslip-row {
          display: flex;
          justify-content: space-between;
          padding: 5px 0;
          font-size: 13px;
          color: #4b5563;
        }
        .payslip-row.total {
          color: #111827;
        }
        .filter-section {
          background: white;
          padding: 20px;
          border-radius: 12px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        .leave-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }
        .leave-stat {
          background: white;
          padding: 20px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        .leave-stat h4 {
          color: #6b7280;
          font-size: 14px;
          margin: 0 0 10px 0;
        }
        .leave-stat p {
          font-size: 24px;
          font-weight: 800;
          margin: 0;
        }
        
        @media print {
          body * {
            visibility: hidden;
          }
          #root, .app, .main-content, .content-container {
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            background: none !important;
          }
          .modal-backdrop {
            background: none !important;
            position: relative !important;
          }
          .modal-content {
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
          }
          .print-payslip-modal, .print-payslip-modal * {
            visibility: visible;
          }
          .print-payslip-modal {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 10px;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PayrollManagement;
